import { BookOpen, Code, Key, Mail, Lock, UserPlus, RefreshCw, Send, Megaphone, Users, Search, Download, Network, Video } from 'lucide-react';

const API_BASE = 'https://unified-auth-admin.pages.dev/api';

function CodeBlock({ code, lang = 'javascript' }: { code: string; lang?: string }) {
  return (
    <pre className="bg-slate-900 rounded-lg p-4 overflow-x-auto text-sm text-green-400 border border-slate-700/50">
      <code>{code}</code>
    </pre>
  );
}

function Section({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) {
  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700/50 overflow-hidden">
      <div className="px-6 py-4 bg-slate-700/30 border-b border-slate-700/50 flex items-center gap-3">
        <Icon className="w-5 h-5 text-blue-400" />
        <h2 className="text-lg font-semibold text-white">{title}</h2>
      </div>
      <div className="p-6 space-y-4">{children}</div>
    </div>
  );
}

export default function ApiDocs() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">API文档</h1>
        <p className="text-slate-400 mt-1">第三方程序连接后台管理系统的完整接口文档</p>
      </div>

      {/* 连接方式 */}
      <Section title="1. 程序连接方式" icon={Key}>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h3 className="text-blue-400 font-semibold mb-2">认证方式</h3>
          <p className="text-slate-300 text-sm mb-3">
            所有第三方API都需要通过 <code className="bg-slate-700 px-1.5 py-0.5 rounded text-yellow-300">app_id</code> 和 <code className="bg-slate-700 px-1.5 py-0.5 rounded text-yellow-300">app_secret</code> 进行认证。
            这两个值在后台「应用管理」中创建应用后自动生成。
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-slate-400 text-xs mb-1">APP_ID（应用ID）</p>
              <code className="text-green-400 text-sm">例如：x7k9m2abc</code>
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1">APP_SECRET（应用密钥）</p>
              <code className="text-green-400 text-sm">例如：a3f8d2...（40位随机字符串）</code>
            </div>
          </div>
        </div>
        <div className="bg-slate-700/30 rounded-lg p-4">
          <h3 className="text-white font-semibold mb-2">API基础地址</h3>
          <code className="text-yellow-300 text-sm">{API_BASE}</code>
        </div>
      </Section>

      {/* 统一账号体系 */}
      <Section title="2. 统一账号体系说明" icon={Users}>
        <div className="space-y-3">
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <h3 className="text-green-400 font-semibold mb-2">核心原则：一个邮箱 = 一个账号</h3>
            <p className="text-slate-300 text-sm mb-3">不同应用共用同一套用户系统。用户在任何应用注册后，其账号在所有应用中通用。</p>
          </div>
          <div className="bg-slate-700/30 rounded-lg p-4 space-y-3">
            <h3 className="text-white font-semibold">跨应用登录流程</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-slate-800 rounded-lg p-3 border border-slate-600/50">
                <h4 className="text-blue-400 text-sm font-medium mb-1">场景：用户在A应用已注册</h4>
                <p className="text-slate-400 text-xs">在B应用使用同一邮箱注册/登录时，系统自动关联到已有账号，<span className="text-green-400">不会提示"已注册"</span>，而是直接登录成功。</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3 border border-slate-600/50">
                <h4 className="text-blue-400 text-sm font-medium mb-1">场景：用户密码在A应用修改</h4>
                <p className="text-slate-400 text-xs">因为共享同一账号，密码修改后在所有应用中生效。</p>
              </div>
            </div>
          </div>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <h3 className="text-yellow-400 font-semibold mb-2">API使用方式</h3>
            <p className="text-slate-300 text-sm">所有应用共用同一套API接口（注册、登录、找回密码、广告），区别仅在于请求参数中的 <code className="bg-slate-700 px-1 py-0.5 rounded text-yellow-300">app_id</code> 和 <code className="bg-slate-700 px-1 py-0.5 rounded text-yellow-300">app_secret</code> 不同。广告API是每个应用独立的，注册/登录/找回密码的API是共享的。</p>
          </div>
        </div>
      </Section>

      {/* 查询用户 */}
      <Section title="3. 查询用户是否已注册" icon={Search}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
            <code className="text-white text-sm">/api/check-user</code>
          </div>
          <p className="text-slate-400 text-sm">查询邮箱是否已注册，可据此决定显示"注册"还是"登录"按钮。</p>
          <table className="w-full text-sm">
            <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
            <tbody className="text-slate-300">
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">email</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">要查询的邮箱</td></tr>
            </tbody>
          </table>
          <CodeBlock code={`// 查询用户示例
const response = await fetch('${API_BASE}/check-user', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID',
    app_secret: 'YOUR_APP_SECRET',
    email: 'user@example.com'
  })
});
const data = await response.json();
// { success: true, data: { exists: true/false, has_password: true/false, linked_to_app: true/false } }`} />
        </div>
      </Section>

      {/* 发送验证码 */}
      <Section title="4. 发送验证码" icon={Send}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
            <code className="text-white text-sm">/api/send-code</code>
          </div>
          <p className="text-slate-400 text-sm">向用户邮箱发送6位数字验证码，有效期5分钟。用于注册、登录、找回密码。</p>
          <div>
            <h4 className="text-slate-300 text-sm font-medium mb-2">请求参数</h4>
            <table className="w-full text-sm">
              <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
              <tbody className="text-slate-300">
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">email</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">用户邮箱</td></tr>
              </tbody>
            </table>
          </div>
          <CodeBlock code={`// 发送验证码示例
const response = await fetch('${API_BASE}/send-code', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID',
    app_secret: 'YOUR_APP_SECRET',
    email: 'user@example.com'
  })
});
const data = await response.json();
// 成功: { success: true, message: '发送成功' }
// 失败: { success: false, message: '发送失败' }`} />
        </div>
      </Section>

      {/* 注册 */}
      <Section title="5. 注册（邮箱+验证码+密码）" icon={UserPlus}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
            <code className="text-white text-sm">/api/register</code>
          </div>
          <p className="text-slate-400 text-sm">用户通过邮箱验证码注册新账号。需先调用 send-code 发送验证码。<span className="text-green-400">如果邮箱已注册，会自动关联到已有账号并登录（不会报"已注册"）</span>。</p>
          <table className="w-full text-sm">
            <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
            <tbody className="text-slate-300">
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">email</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">用户邮箱</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">code</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">邮箱验证码</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">password</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">用户密码</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">nickname</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">否</td><td className="py-2 px-3">用户昵称</td></tr>
            </tbody>
          </table>
          <CodeBlock code={`// 注册流程示例
// 第1步: 发送验证码
await fetch('${API_BASE}/send-code', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID',
    app_secret: 'YOUR_APP_SECRET',
    email: 'user@example.com'
  })
});

// 第2步: 用户输入收到的验证码后，调用注册接口
const response = await fetch('${API_BASE}/register', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID',
    app_secret: 'YOUR_APP_SECRET',
    email: 'user@example.com',
    code: '123456',
    password: 'user_password',
    nickname: '用户昵称'
  })
});
const data = await response.json();
// 成功: { success: true, data: { user: {...}, token: "..." } }
// 失败: { success: false, message: '验证码错误或已过期' }`} />
        </div>
      </Section>

      {/* 密码登录 */}
      <Section title="6. 密码登录" icon={Lock}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
            <code className="text-white text-sm">/api/login</code>
          </div>
          <p className="text-slate-400 text-sm">已注册用户使用邮箱+密码登录。</p>
          <table className="w-full text-sm">
            <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
            <tbody className="text-slate-300">
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">email</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">用户邮箱</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">password</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">用户密码</td></tr>
            </tbody>
          </table>
          <CodeBlock code={`// 密码登录示例
const response = await fetch('${API_BASE}/login', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID',
    app_secret: 'YOUR_APP_SECRET',
    email: 'user@example.com',
    password: 'user_password'
  })
});
const data = await response.json();
// 成功: { success: true, data: { user: {...}, token: "..." } }
// 失败: { success: false, message: '邮箱或密码错误' }`} />
        </div>
      </Section>

      {/* 验证码登录 */}
      <Section title="7. 验证码登录（免密码）" icon={Mail}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
            <code className="text-white text-sm">/api/verify-login</code>
          </div>
          <p className="text-slate-400 text-sm">用户使用邮箱验证码直接登录。如果用户不存在则自动注册（无需密码）。</p>
          <table className="w-full text-sm">
            <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
            <tbody className="text-slate-300">
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">email</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">用户邮箱</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">code</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">邮箱验证码</td></tr>
            </tbody>
          </table>
          <CodeBlock code={`// 验证码登录示例（先调用send-code发送验证码）
const response = await fetch('${API_BASE}/verify-login', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID',
    app_secret: 'YOUR_APP_SECRET',
    email: 'user@example.com',
    code: '123456'
  })
});
const data = await response.json();
// 成功: { success: true, data: { user: {...}, is_new_user: true/false } }`} />
        </div>
      </Section>

      {/* 找回密码 */}
      <Section title="8. 找回/重置密码" icon={RefreshCw}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
            <code className="text-white text-sm">/api/reset-password</code>
          </div>
          <p className="text-slate-400 text-sm">用户通过邮箱验证码重置密码。需先调用 send-code 发送验证码。</p>
          <table className="w-full text-sm">
            <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
            <tbody className="text-slate-300">
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">email</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">用户邮箱</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">code</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">邮箱验证码</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">new_password</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">新密码</td></tr>
            </tbody>
          </table>
          <CodeBlock code={`// 重置密码流程示例
// 第1步: 发送验证码
await fetch('${API_BASE}/send-code', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID',
    app_secret: 'YOUR_APP_SECRET',
    email: 'user@example.com'
  })
});

// 第2步: 用户输入验证码和新密码
const response = await fetch('${API_BASE}/reset-password', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID',
    app_secret: 'YOUR_APP_SECRET',
    email: 'user@example.com',
    code: '123456',
    new_password: 'new_password_123'
  })
});
const data = await response.json();
// 成功: { success: true, message: '密码重置成功' }
// 失败: { success: false, message: '验证码错误或已过期' }`} />
        </div>
      </Section>

      {/* 第三方用户管理API */}
      <Section title="9. 第三方用户管理API（程序后台使用）" icon={Users}>
        <div className="space-y-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <h3 className="text-red-400 font-semibold mb-2">适用场景</h3>
            <p className="text-slate-300 text-sm">如果你的程序有自己的后台管理系统，需要在自己的后台中显示和管理来自统一账号系统的用户，可以使用以下API。这些API需要 <code className="bg-slate-700 px-1 py-0.5 rounded text-yellow-300">app_id</code> + <code className="bg-slate-700 px-1 py-0.5 rounded text-yellow-300">app_secret</code> 认证，且只能管理关联到当前应用的用户。</p>
          </div>

          {/* 授权说明 */}
          <div className="bg-slate-700/30 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-2">授权与安全</h3>
            <ul className="text-slate-300 text-sm space-y-1">
              <li>• 所有管理API都需要 <code className="bg-slate-700 px-1 py-0.5 rounded text-yellow-300">app_id</code> + <code className="bg-slate-700 px-1 py-0.5 rounded text-yellow-300">app_secret</code> 认证</li>
              <li>• 只能查看和管理已关联到当前应用的用户</li>
              <li>• <code className="bg-slate-700 px-1 py-0.5 rounded text-yellow-300">app_secret</code> 必须保管在服务端，切勿暴露给前端</li>
              <li>• 禁用用户后，该用户在所有应用中都无法登录</li>
            </ul>
          </div>

          {/* 9.1 获取用户列表 */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <h3 className="text-white font-semibold mb-2">9.1 获取应用用户列表</h3>
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
              <code className="text-white text-sm">/api/app/users</code>
            </div>
            <table className="w-full text-sm mb-3">
              <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
              <tbody className="text-slate-300">
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">page</code></td><td className="py-2 px-3">number</td><td className="py-2 px-3">否</td><td className="py-2 px-3">页码，默认1</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">page_size</code></td><td className="py-2 px-3">number</td><td className="py-2 px-3">否</td><td className="py-2 px-3">每页数量，默认20</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">keyword</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">否</td><td className="py-2 px-3">搜索关键词（邮箱/昵称）</td></tr>
              </tbody>
            </table>
            <CodeBlock code={`// 获取应用用户列表示例
const response = await fetch('${API_BASE}/app/users', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID',
    app_secret: 'YOUR_APP_SECRET',
    page: 1,
    page_size: 20,
    keyword: '搜索关键词'
  })
});
const data = await response.json();
// { success: true, data: { list: [...], total: 100, page: 1, page_size: 20, total_pages: 5 } }

// 返回的用户对象字段
// { id, email, nickname, email_verified, status, created_at, updated_at }`} />
          </div>

          {/* 9.2 获取用户详情 */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <h3 className="text-white font-semibold mb-2">9.2 获取用户详情</h3>
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
              <code className="text-white text-sm">/api/app/user/detail</code>
            </div>
            <table className="w-full text-sm mb-3">
              <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
              <tbody className="text-slate-300">
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">user_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">否</td><td className="py-2 px-3">用户ID（与email二选一）</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">email</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">否</td><td className="py-2 px-3">用户邮箱（与user_id二选一）</td></tr>
              </tbody>
            </table>
            <CodeBlock code={`const response = await fetch('${API_BASE}/app/user/detail', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID', app_secret: 'YOUR_APP_SECRET',
    user_id: '用户ID' // 或 email: 'user@example.com'
  })
});
// { success: true, data: { id, email, nickname, email_verified, has_password, status, linked_to_app, created_at, updated_at } }`} />
          </div>

          {/* 9.3 更新用户 */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <h3 className="text-white font-semibold mb-2">9.3 更新用户信息</h3>
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
              <code className="text-white text-sm">/api/app/user/update</code>
            </div>
            <table className="w-full text-sm mb-3">
              <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
              <tbody className="text-slate-300">
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">user_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">用户ID</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">nickname</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">否</td><td className="py-2 px-3">新昵称</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">status</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">否</td><td className="py-2 px-3">状态：active/disabled</td></tr>
              </tbody>
            </table>
            <CodeBlock code={`const response = await fetch('${API_BASE}/app/user/update', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID', app_secret: 'YOUR_APP_SECRET',
    user_id: '用户ID', nickname: '新昵称', status: 'active'
  })
});
// { success: true, data: { id, email, nickname, status, updated_at } }`} />
          </div>

          {/* 9.4 禁用/启用 */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <h3 className="text-white font-semibold mb-2">9.4 禁用/启用用户</h3>
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
              <code className="text-white text-sm">/api/app/user/toggle-status</code>
            </div>
            <table className="w-full text-sm mb-3">
              <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
              <tbody className="text-slate-300">
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">user_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">用户ID</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">status</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">active（启用）或 disabled（禁用）</td></tr>
              </tbody>
            </table>
            <CodeBlock code={`// 禁用用户
await fetch('${API_BASE}/app/user/toggle-status', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID', app_secret: 'YOUR_APP_SECRET',
    user_id: '用户ID', status: 'disabled'
  })
});
// { success: true, message: '用户已禁用' }

// 启用用户
await fetch('${API_BASE}/app/user/toggle-status', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID', app_secret: 'YOUR_APP_SECRET',
    user_id: '用户ID', status: 'active'
  })
});
// { success: true, message: '用户已启用' }`} />
          </div>

          {/* 9.5 移除用户 */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <h3 className="text-white font-semibold mb-2">9.5 从应用中移除用户</h3>
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
              <code className="text-white text-sm">/api/app/user/unlink</code>
            </div>
            <p className="text-slate-400 text-sm mb-3">解除用户与当前应用的关联。用户账号不会被删除，只是不再出现在当前应用的用户列表中。</p>
            <table className="w-full text-sm mb-3">
              <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
              <tbody className="text-slate-300">
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">user_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">用户ID</td></tr>
              </tbody>
            </table>
            <CodeBlock code={`const response = await fetch('${API_BASE}/app/user/unlink', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    app_id: 'YOUR_APP_ID', app_secret: 'YOUR_APP_SECRET',
    user_id: '用户ID'
  })
});
// { success: true, message: '已从应用中移除该用户' }`} />
          </div>

          {/* 9.6 验证Token */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <h3 className="text-white font-semibold mb-2">9.6 验证用户Token</h3>
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded">POST</span>
              <code className="text-white text-sm">/api/app/verify-token</code>
            </div>
            <p className="text-slate-400 text-sm mb-3">在你的程序后端验证前端传来的用户Token是否有效。用于保护需要登录才能访问的API。</p>
            <table className="w-full text-sm mb-3">
              <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
              <tbody className="text-slate-300">
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_secret</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用密钥</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">user_token</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">用户登录后获取的Token</td></tr>
              </tbody>
            </table>
            <CodeBlock code={`// 你的后端API中间件示例（Node.js/Express）
app.use('/api/protected/*', async (req, res, next) => {
  const userToken = req.headers.authorization?.replace('Bearer ', '');
  if (!userToken) return res.status(401).json({ message: '未登录' });

  // 调用统一账号系统验证Token
  const response = await fetch('${API_BASE}/app/verify-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      app_id: 'YOUR_APP_ID',
      app_secret: 'YOUR_APP_SECRET',
      user_token: userToken
    })
  });
  const data = await response.json();
  if (!data.success) return res.status(401).json({ message: data.message });

  // Token有效，将用户信息挂载到请求上
  req.user = data.data;
  next();
});`} />
          </div>
        </div>
      </Section>

      {/* 获取广告 */}
      <Section title="10. 广告管理API" icon={Megaphone}>
        <div className="space-y-4">
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
            <h3 className="text-purple-400 font-semibold mb-2">广告系统说明</h3>
            <ul className="text-slate-300 text-sm space-y-1">
              <li>• 在后台「广告管理」中为指定应用创建广告</li>
              <li>• 广告按应用隔离，每个应用可独立配置不同广告</li>
              <li>• 广告API只需 <code className="bg-slate-700 px-1 py-0.5 rounded text-yellow-300">app_id</code>，无需 <code className="bg-slate-700 px-1 py-0.5 rounded text-yellow-300">app_secret</code>（公开接口）</li>
              <li>• 支持设置广告投放时间段、启用/禁用状态</li>
            </ul>
          </div>

          <h3 className="text-white font-semibold">广告位置类型</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-700/30 rounded-lg p-3 border border-slate-600/50">
              <h4 className="text-blue-400 text-sm font-medium mb-1">top - 顶部横幅</h4>
              <p className="text-slate-400 text-xs">页面顶部固定的横幅广告，适合品牌曝光</p>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-3 border border-slate-600/50">
              <h4 className="text-blue-400 text-sm font-medium mb-1">bottom - 底部横幅</h4>
              <p className="text-slate-400 text-xs">页面底部固定的横幅广告，适合推广引流</p>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-3 border border-slate-600/50">
              <h4 className="text-blue-400 text-sm font-medium mb-1">popup - 弹窗广告</h4>
              <p className="text-slate-400 text-xs">居中弹窗展示，适合重要通知和活动推广</p>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-3 border border-slate-600/50">
              <h4 className="text-blue-400 text-sm font-medium mb-1">banner - 内嵌横幅</h4>
              <p className="text-slate-400 text-xs">内容区域内嵌展示，适合信息流广告</p>
            </div>
            <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/30">
              <h4 className="text-purple-400 text-sm font-medium mb-1">preroll - 前贴片广告</h4>
              <p className="text-slate-400 text-xs">正片播放前的视频/图片广告，支持倒计时跳过</p>
            </div>
            <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/30">
              <h4 className="text-purple-400 text-sm font-medium mb-1">midroll - 中插广告</h4>
              <p className="text-slate-400 text-xs">正片播放到指定时间点插入广告</p>
            </div>
            <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/30">
              <h4 className="text-purple-400 text-sm font-medium mb-1">overlay - 播放器覆盖层</h4>
              <p className="text-slate-400 text-xs">播放器右下角小广告，定时自动消失</p>
            </div>
            <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/30">
              <h4 className="text-purple-400 text-sm font-medium mb-1">splash - 开屏广告</h4>
              <p className="text-slate-400 text-xs">页面/应用打开时全屏广告，倒计时后自动关闭</p>
            </div>
          </div>

          <h3 className="text-white font-semibold mt-4">视频广告扩展字段</h3>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <p className="text-amber-400 text-sm font-semibold mb-2">当 ad_type=video 时启用以下字段：</p>
            <table className="w-full text-sm">
              <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">字段</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">说明</th></tr></thead>
              <tbody className="text-slate-300">
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">ad_type</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">广告类型：<code className="text-green-400">image</code>（默认）或 <code className="text-purple-400">video</code></td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">video_url</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">视频广告 URL（MP4/WebM 等）</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">duration</code></td><td className="py-2 px-3">number</td><td className="py-2 px-3">广告时长（秒），用于前贴片/中插/开屏</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">skip_after</code></td><td className="py-2 px-3">number</td><td className="py-2 px-3">多少秒后可跳过（默认 5）</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">auto_close</code></td><td className="py-2 px-3">boolean</td><td className="py-2 px-3">倒计时结束后是否自动关闭（默认 true）</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">midroll_time</code></td><td className="py-2 px-3">number</td><td className="py-2 px-3">中插广告：正片第几秒插入</td></tr>
                <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">close_after</code></td><td className="py-2 px-3">number</td><td className="py-2 px-3">覆盖层：多少秒后自动关闭</td></tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-white font-semibold">获取广告列表</h3>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs font-bold rounded">GET</span>
            <code className="text-white text-sm">/api/ads/public?app_id=xxx&amp;position=banner</code>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">参数</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">必填</th><th className="text-left py-2 px-3">说明</th></tr></thead>
            <tbody className="text-slate-300">
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">app_id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">是</td><td className="py-2 px-3">应用ID</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">position</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">否</td><td className="py-2 px-3">广告位置：top/bottom/popup/banner</td></tr>
            </tbody>
          </table>

          <h3 className="text-white font-semibold">返回字段说明</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-slate-400"><th className="text-left py-2 px-3">字段</th><th className="text-left py-2 px-3">类型</th><th className="text-left py-2 px-3">说明</th></tr></thead>
            <tbody className="text-slate-300">
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">id</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">广告ID</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">title</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">广告标题</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">image_url</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">广告图片URL</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">link_url</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">点击跳转链接</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">position</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">位置：top/bottom/popup/banner</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">sort_order</code></td><td className="py-2 px-3">number</td><td className="py-2 px-3">排序（小的在前）</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">start_time</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">投放开始时间</td></tr>
              <tr className="border-t border-slate-700/50"><td className="py-2 px-3"><code className="text-yellow-300">end_time</code></td><td className="py-2 px-3">string</td><td className="py-2 px-3">投放结束时间</td></tr>
            </tbody>
          </table>

          <CodeBlock code={`// 获取广告列表示例
const response = await fetch('${API_BASE}/ads/public?app_id=YOUR_APP_ID&position=banner');
const data = await response.json();
// 成功: { success: true, data: [{ id, title, image_url, link_url, position, sort_order, ... }] }

// 前端渲染广告示例
if (data.success && data.data.length > 0) {
  data.data.forEach(ad => {
    console.log(\`广告: \${ad.title}, 图片: \${ad.image_url}, 链接: \${ad.link_url}\`);
  });
}

// 按位置分别获取
const topAds = await fetch('${API_BASE}/ads/public?app_id=YOUR_APP_ID&position=top');
const popupAds = await fetch('${API_BASE}/ads/public?app_id=YOUR_APP_ID&position=popup');

// 获取所有位置的广告（不传position参数）
const allAds = await fetch('${API_BASE}/ads/public?app_id=YOUR_APP_ID');`} />

          <h3 className="text-white font-semibold">前端渲染建议</h3>
          <CodeBlock code={`// React 渲染广告示例
function AdBanner({ appId, position }) {
  const [ads, setAds] = useState([]);

  useEffect(() => {
    fetch(\`${API_BASE}/ads/public?app_id=\${appId}&position=\${position}\`)
      .then(r => r.json())
      .then(data => { if (data.success) setAds(data.data); });
  }, [appId, position]);

  if (ads.length === 0) return null;

  // 顶部/底部横幅
  if (position === 'top' || position === 'bottom') {
    return (
      <div style={{ position: position === 'top' ? 'sticky' : 'fixed', top: position === 'top' ? 0 : 'auto', bottom: position === 'bottom' ? 0 : 'auto', width: '100%', zIndex: 100 }}>
        {ads.map(ad => (
          <a key={ad.id} href={ad.link_url} target="_blank">
            <img src={ad.image_url} alt={ad.title} style={{ width: '100%', height: '60px', objectFit: 'cover' }} />
          </a>
        ))}
      </div>
    );
  }

  // 弹窗广告
  if (position === 'popup') {
    const ad = ads[0];
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: '#fff', borderRadius: 12, maxWidth: 300, overflow: 'hidden' }}>
          <a href={ad.link_url} target="_blank">
            <img src={ad.image_url} alt={ad.title} style={{ width: '100%', height: 150, objectFit: 'cover' }} />
          </a>
          <div style={{ padding: 12 }}>
            <h3>{ad.title}</h3>
            <button onClick={() => setAds([])}>关闭</button>
          </div>
        </div>
      </div>
    );
  }

  // 内嵌横幅
  return (
    <div style={{ padding: 12 }}>
      {ads.map(ad => (
        <a key={ad.id} href={ad.link_url} target="_blank" style={{ display: 'block', marginBottom: 12, borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <img src={ad.image_url} alt={ad.title} style={{ width: '100%', height: 120, objectFit: 'cover' }} />
          <div style={{ padding: 8 }}>{ad.title}</div>
        </a>
      ))}
    </div>
  );
}`} />

          {/* 广告联盟配置 API */}
          <h3 className="text-white font-semibold mt-6 flex items-center gap-2">
            <Network className="w-5 h-5 text-blue-400" /> 广告联盟配置 API
          </h3>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-blue-300 text-sm font-semibold mb-2">支持 8 个广告联盟平台：</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-slate-400"><th className="text-left py-2 px-2">platform</th><th className="text-left py-2 px-2">平台</th><th className="text-left py-2 px-2">资质</th><th className="text-left py-2 px-2">类型</th><th className="text-left py-2 px-2">适合</th></tr></thead>
                <tbody className="text-slate-300">
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2"><code className="text-yellow-300">none</code></td><td className="py-2 px-2">不接入</td><td className="py-2 px-2">—</td><td className="py-2 px-2">—</td><td className="py-2 px-2">仅自托管广告</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2"><code className="text-yellow-300">csj</code></td><td className="py-2 px-2">穿山甲</td><td className="py-2 px-2 text-amber-400">需公司</td><td className="py-2 px-2 text-purple-400">原生APP</td><td className="py-2 px-2">APP视频广告</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2"><code className="text-yellow-300">ylh</code></td><td className="py-2 px-2">优量汇</td><td className="py-2 px-2 text-amber-400">需公司</td><td className="py-2 px-2 text-purple-400">原生APP</td><td className="py-2 px-2">微信生态</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2"><code className="text-yellow-300">baidu</code></td><td className="py-2 px-2">百度联盟</td><td className="py-2 px-2 text-green-400">个人可接</td><td className="py-2 px-2 text-blue-400">网站/H5</td><td className="py-2 px-2">网站流量</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2"><code className="text-yellow-300">adsense</code></td><td className="py-2 px-2">Google AdSense</td><td className="py-2 px-2 text-green-400">个人可接</td><td className="py-2 px-2 text-blue-400">网站/H5</td><td className="py-2 px-2">收益最高</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2"><code className="text-yellow-300">mango</code></td><td className="py-2 px-2">芒果联盟</td><td className="py-2 px-2 text-green-400">个人可接</td><td className="py-2 px-2 text-purple-400">原生APP</td><td className="py-2 px-2">聚合20+联盟</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2"><code className="text-yellow-300">baiyi</code></td><td className="py-2 px-2">百益聚合</td><td className="py-2 px-2 text-green-400">个人可接</td><td className="py-2 px-2 text-purple-400">原生APP</td><td className="py-2 px-2">无软著可接</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2"><code className="text-yellow-300">pocket</code></td><td className="py-2 px-2">口袋工厂</td><td className="py-2 px-2 text-green-400">个人可接</td><td className="py-2 px-2 text-purple-400">原生APP</td><td className="py-2 px-2">数据透明</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-slate-400 text-xs mt-2">
              个人开发者：APP 端推荐 <code className="text-green-400">mango</code>/<code className="text-green-400">baiyi</code>/<code className="text-green-400">pocket</code>（聚合平台，接一个即可）；Web 端推荐 <code className="text-green-400">adsense</code>/<code className="text-green-400">baidu</code>。
              原生 APP 接入详见 <code className="text-yellow-300">聚合广告平台接入指南.md</code>。
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs font-bold rounded">GET</span>
              <code className="text-white text-sm">/api/ad-network/public?app_id=xxx</code>
              <span className="text-slate-500 text-xs">公开接口，供 SDK 调用，无需鉴权</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs font-bold rounded">GET</span>
              <code className="text-white text-sm">/api/ad-network?app_id=xxx</code>
              <span className="text-slate-500 text-xs">管理接口，需 Bearer Token</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-amber-500/20 text-amber-300 text-xs font-bold rounded">PUT</span>
              <code className="text-white text-sm">/api/ad-network</code>
              <span className="text-slate-500 text-xs">更新配置，需 Bearer Token</span>
            </div>
          </div>

          <p className="text-slate-400 text-sm">更新配置 Body（按所选平台填写对应字段）：</p>
          <CodeBlock code={`{
  "app_id": "xxx",
  "enabled": true,                // 联盟总开关
  "platform": "mango",            // none | csj | ylh | baidu | adsense | mango | baiyi | pocket
  "fallback_to_self": true,       // 无填充时回退自托管广告

  // 穿山甲（需公司资质）
  "csj_app_id": "", "csj_preroll_slot": "", "csj_splash_slot": "", "csj_interstitial_slot": "",
  // 优量汇（需公司资质）
  "ylh_app_id": "", "ylh_preroll_slot": "", "ylh_splash_slot": "", "ylh_interstitial_slot": "",
  // 百度联盟（个人可接入，注册：union.baidu.com）
  "baidu_union_id": "", "baidu_preroll_slot": "", "baidu_splash_slot": "", "baidu_banner_slot": "",
  // Google AdSense（个人可接入，注册：adsense.google.com）
  "adsense_client_id": "ca-pub-xxxxxxxx", "adsense_preroll_slot": "", "adsense_splash_slot": "", "adsense_banner_slot": "",
  // 芒果联盟（个人可接入，原生APP，聚合穿山甲/优量汇/快手等20+联盟）
  "mango_app_id": "", "mango_splash_id": "", "mango_banner_id": "", "mango_interstitial_id": "", "mango_reward_video_id": "",
  "mango_short_video_id": "", "mango_fullscreen_video_id": "", "mango_feed_id": "",
  // 百益聚合（个人可接入，原生APP，无需软著）
  "baiyi_app_id": "", "baiyi_splash_id": "", "baiyi_banner_id": "", "baiyi_interstitial_id": "", "baiyi_reward_video_id": "",
  "baiyi_short_video_id": "", "baiyi_fullscreen_video_id": "", "baiyi_feed_id": "",
  // 口袋工厂（个人可接入，原生APP，聚合优量汇/穿山甲/快手）
  "pocket_app_id": "", "pocket_splash_id": "", "pocket_banner_id": "", "pocket_interstitial_id": "", "pocket_reward_video_id": "",
  "pocket_short_video_id": "", "pocket_fullscreen_video_id": "", "pocket_feed_id": ""
}`} />

          {/* 广告位类型说明 */}
          <div className="bg-slate-700/30 rounded-lg p-4 mt-3">
            <h4 className="text-white text-sm font-semibold mb-2">广告位类型对照表</h4>
            <p className="text-slate-400 text-xs mb-3">芒果/百益/口袋工厂 支持 8 种广告位，字段名格式为 <code className="text-yellow-300">{'{platform}_{type}'}</code></p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-slate-400"><th className="text-left py-2 px-2">广告位类型</th><th className="text-left py-2 px-2">字段后缀</th><th className="text-left py-2 px-2">说明</th><th className="text-left py-2 px-2">示例（芒果）</th></tr></thead>
                <tbody className="text-slate-300">
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2">开屏</td><td className="py-2 px-2"><code className="text-green-300">splash_id</code></td><td className="py-2 px-2">应用启动全屏广告</td><td className="py-2 px-2">mango_splash_id</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2">横幅</td><td className="py-2 px-2"><code className="text-green-300">banner_id</code></td><td className="py-2 px-2">页面顶部/底部条形广告</td><td className="py-2 px-2">mango_banner_id</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2">插屏</td><td className="py-2 px-2"><code className="text-green-300">interstitial_id</code></td><td className="py-2 px-2">弹窗插屏广告</td><td className="py-2 px-2">mango_interstitial_id</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2">激励视频</td><td className="py-2 px-2"><code className="text-green-300">reward_video_id</code></td><td className="py-2 px-2">看完视频发奖励</td><td className="py-2 px-2">mango_reward_video_id</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2">短视频</td><td className="py-2 px-2"><code className="text-green-300">short_video_id</code></td><td className="py-2 px-2">短视频信息流广告</td><td className="py-2 px-2">mango_short_video_id</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2">全屏视频</td><td className="py-2 px-2"><code className="text-green-300">fullscreen_video_id</code></td><td className="py-2 px-2">全屏视频广告（自动播放）</td><td className="py-2 px-2">mango_fullscreen_video_id</td></tr>
                  <tr className="border-t border-slate-700/50"><td className="py-2 px-2">信息流</td><td className="py-2 px-2"><code className="text-green-300">feed_id</code></td><td className="py-2 px-2">内容列表中插入广告</td><td className="py-2 px-2">mango_feed_id</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-slate-500 text-xs mt-2">百益聚合：baiyi_splash_id / baiyi_banner_id / ... / baiyi_feed_id &nbsp;|&nbsp; 口袋工厂：pocket_splash_id / pocket_banner_id / ... / pocket_feed_id</p>
          </div>

          {/* 广告 SDK 集成 */}
          <h3 className="text-white font-semibold mt-6 flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-400" /> 广告 SDK 集成（ad-widget.js）
          </h3>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
            <p className="text-purple-300 text-sm font-semibold mb-2">SDK 自动完成：</p>
            <ul className="text-slate-300 text-sm space-y-1">
              <li>• 读取联盟配置 → 加载对应平台 SDK（AdSense/百度联盟/穿山甲/优量汇）</li>
              <li>• 开屏广告：页面加载自动全屏展示，倒计时后关闭</li>
              <li>• 前贴片/中插/覆盖层：视频播放器集成，倒计时+可跳过</li>
              <li>• 联盟广告优先，无填充回退自托管广告</li>
            </ul>
          </div>

          <p className="text-slate-400 text-xs mb-1">方式 A：自动初始化（推荐）</p>
          <CodeBlock code={`<script
  src="https://unified-auth-admin.pages.dev/sdk/ad-widget.js"
  data-app-id="YOUR_APP_ID"
  defer
></script>`} />

          <p className="text-slate-400 text-xs mb-1 mt-3">方式 B：手动初始化 + 视频播放器集成</p>
          <CodeBlock code={`<script src="https://unified-auth-admin.pages.dev/sdk/ad-widget.js"></script>
<script>
  var ad = new AdWidget({ appId: 'YOUR_APP_ID' });
  ad.init();  // 自动展示开屏广告

  // 视频播放器集成
  var video = document.getElementById('my-video');
  ad.showPreroll(video, function() { video.play(); });  // 前贴片
  ad.scheduleMidroll(video);                             // 中插（按 midroll_time）
  ad.showOverlay(video);                                  // 覆盖层
  ad.showBanner('top');                                   // 顶部横幅
</script>`} />

          {/* Android APP SDK 集成 */}
          <h3 className="text-white font-semibold mt-6 flex items-center gap-2">
            <Video className="w-5 h-5 text-green-400" /> Android APP SDK 集成（原生广告）
          </h3>
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <p className="text-green-300 text-sm font-semibold mb-2">
              芒果联盟 / 百益聚合 / 口袋工厂 均为原生 APP SDK，使用 Android SDK 模块自动接入：
            </p>
            <ul className="text-slate-300 text-sm space-y-1">
              <li>• APP 启动时自动调用后台 API 拉取广告联盟配置</li>
              <li>• 根据后台配置的 platform 自动初始化对应平台 SDK（芒果/百益/口袋工厂）</li>
              <li>• 自动加载开屏 / 横幅 / 插屏 / 激励视频广告</li>
              <li>• 联盟广告失败时自动回退自托管广告（调用 GET /api/ads/public）</li>
              <li>• 后台切换广告平台，APP 无需重新发版</li>
              <li>• SDK 模块位于项目 <code className="text-yellow-300">android-sdk/</code> 目录，接入文档见 <code className="text-yellow-300">android-sdk/README.md</code></li>
            </ul>
          </div>

          <p className="text-slate-400 text-xs mb-1 mt-3">第 1 步：Application 中初始化（只需一行）</p>
          <CodeBlock code={`public class MyApp extends Application {
    @Override
    public void onCreate() {
        super.onCreate();
        // 自动从后台拉取配置 + 初始化对应平台 SDK
        AdNetworkManager.init(this, "YOUR_APP_ID", "https://unified-auth-admin.pages.dev");
    }
}`} />

          <p className="text-slate-400 text-xs mb-1 mt-3">第 2 步：加载广告（支持 7 种广告位）</p>
          <CodeBlock code={`// 开屏广告
AdNetworkManager.loadSplash(this, new AdListener() {
    @Override public void onAdClosed() { goToMain(); }      // 广告关闭 → 进入主界面
    @Override public void onAdError(int code, String msg) { goToMain(); }  // 失败 → 也进入
});

// 激励视频（看完发奖）
AdNetworkManager.loadRewardVideo(this, new AdListener() {
    @Override public void onAdReward() { giveUserReward(); }
});

// 横幅广告
AdNetworkManager.loadBanner(this, bannerContainer, null);

// 插屏广告
AdNetworkManager.loadInterstitial(this, null);

// 短视频广告
AdNetworkManager.loadShortVideo(this, null);

// 全屏视频广告
AdNetworkManager.loadFullscreenVideo(this, null);

// 信息流广告（嵌入内容列表）
AdNetworkManager.loadFeed(this, feedContainer, null);`} />

          <p className="text-slate-400 text-xs mb-1 mt-3">第 3 步（可选）：设置自托管广告回退</p>
          <CodeBlock code={`AdNetworkManager.setFallbackHandler(type -> {
    // 联盟广告失败时回调，type: SPLASH / BANNER / INTERSTITIAL / REWARD_VIDEO
    //   / SHORT_VIDEO / FULLSCREEN_VIDEO / NATIVE_FLOW
    // 调用 GET /api/ads/public?app_id=YOUR_APP_ID 获取自托管广告并展示
    loadSelfHostedAd(type);
});`} />

          <div className="bg-slate-700/30 rounded-lg p-3 text-xs text-slate-400 mt-3">
            <strong className="text-slate-300">SDK 工作流程：</strong>
            <code className="text-blue-400">init()</code> → 调用
            <code className="text-yellow-300">GET /api/ad-network/public?app_id=xxx</code>
            拉取配置 → 反射加载 <code className="text-purple-300">MangoAdAdapter</code> /
            <code className="text-purple-300">BaiyiAdAdapter</code> /
            <code className="text-purple-300">PocketAdAdapter</code>
            → 初始化平台 SDK → 加载广告。未集成的平台自动回退，不影响编译。
          </div>
        </div>
      </Section>

      {/* 完整流程 */}
      <Section title="11. 完整接入流程" icon={Code}>
        <div className="space-y-4">
          <div className="bg-slate-700/30 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-3">步骤一：在后台创建应用</h3>
            <p className="text-slate-400 text-sm">登录后台管理 → 应用管理 → 新建应用 → 获取 APP_ID 和 APP_SECRET</p>
          </div>
          <div className="bg-slate-700/30 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-3">步骤二：在程序中配置</h3>
            <CodeBlock code={`// 配置文件
const CONFIG = {
  API_BASE: '${API_BASE}',
  APP_ID: 'YOUR_APP_ID',
  APP_SECRET: 'YOUR_APP_SECRET'
};`} />
          </div>
          <div className="bg-slate-700/30 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-3">步骤三：实现注册/登录功能</h3>
            <CodeBlock code={`// 通用请求封装
async function apiCall(endpoint: string, data: object) {
  const response = await fetch(\`\${CONFIG.API_BASE}\${endpoint}\`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...data, app_id: CONFIG.APP_ID, app_secret: CONFIG.APP_SECRET })
  });
  return response.json();
}

// 1. 发送验证码
async function sendVerifyCode(email: string) {
  return apiCall('/send-code', { email });
}

// 2. 注册
async function register(email: string, code: string, password: string, nickname?: string) {
  return apiCall('/register', { email, code, password, nickname });
}

// 3. 密码登录
async function loginWithPassword(email: string, password: string) {
  return apiCall('/login', { email, password });
}

// 4. 验证码登录
async function loginWithCode(email: string, code: string) {
  return apiCall('/verify-login', { email, code });
}

// 5. 重置密码
async function resetPassword(email: string, code: string, newPassword: string) {
  return apiCall('/reset-password', { email, code, new_password: newPassword });
}

// 6. 获取广告
async function getAds(position?: string) {
  const url = \`\${CONFIG.API_BASE}/ads/public?app_id=\${CONFIG.APP_ID}\${position ? '&position=' + position : ''}\`;
  const response = await fetch(url);
  return response.json();
}`} />
          </div>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <h3 className="text-yellow-400 font-semibold mb-2">安全提醒</h3>
            <ul className="text-slate-300 text-sm space-y-1">
              <li>• APP_SECRET 切勿暴露在前端代码中，建议由后端服务器转发API请求</li>
              <li>• 验证码有效期5分钟，过期需重新发送</li>
              <li>• 同一邮箱1分钟内只能发送1次验证码</li>
              <li>• 密码长度建议至少6位</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* 12. 客户端下载 API */}
      <Section title="12. 客户端下载 API" icon={Download}>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h3 className="text-blue-400 font-semibold mb-2">功能说明</h3>
          <p className="text-slate-300 text-sm">
            为每个应用配置 App / PC / TV 三端客户端下载。每端支持三种模式（可独立开关）：
            <span className="text-green-400">网址直达</span>（点击跳转URL）、
            <span className="text-purple-400">二维码扫码</span>（上传二维码图片）、
            <span className="text-amber-400">文件下载</span>（上传安装包到云端存储）。
            配置完成后，各程序前端引入 SDK 即可在首页左上方显示下载下拉窗口。
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-white font-semibold">公开接口（供 SDK 调用，无需鉴权）</h3>

          <div className="bg-slate-700/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded">GET</span>
              <code className="text-yellow-300 text-sm">/downloads/public/:app_id</code>
            </div>
            <p className="text-slate-400 text-sm mb-3">获取指定应用所有已启用平台的下载配置（仅返回已开启的模式，隐藏内部字段）</p>
            <CodeBlock code={`fetch('https://unified-auth-admin.pages.dev/api/downloads/public/{APP_ID}')
  .then(r => r.json())
  .then(res => {
    // res.data: 数组，每项为一个平台的下载配置
    // {
    //   platform: 'app' | 'pc' | 'tv',
    //   mode_url_enabled: true,      // 网址直达是否启用
    //   mode_qrcode_enabled: true,   // 二维码是否启用
    //   mode_file_enabled: true,     // 文件下载是否启用
    //   url: 'https://...',          // 直达URL（仅当mode_url_enabled）
    //   qrcode_url: 'https://...',   // 二维码图片URL（仅当mode_qrcode_enabled）
    //   file_id: 'xxx',              // 文件ID（仅当mode_file_enabled）
    //   file_name: 'app.apk',
    //   file_size: 12345678,
    //   file_version: 'v1.0.0'
    // }
  });`} />
          </div>

          <div className="bg-slate-700/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded">GET</span>
              <code className="text-yellow-300 text-sm">/downloads/file/:id</code>
            </div>
            <p className="text-slate-400 text-sm">下载安装包文件（直接返回二进制流，浏览器会触发下载）</p>
            <CodeBlock code={`// 直接作为链接使用
window.open('https://unified-auth-admin.pages.dev/api/downloads/file/{FILE_ID}');
// 或
<a href="https://unified-auth-admin.pages.dev/api/downloads/file/{FILE_ID}">下载</a>`} />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-white font-semibold">管理接口（需管理员 Bearer Token）</h3>

          <div className="bg-slate-700/30 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded">GET</span>
              <code className="text-yellow-300 text-sm">/downloads?app_id=xxx</code>
              <span className="text-slate-500 text-xs">列出下载配置（可按应用筛选）</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded">POST</span>
              <code className="text-yellow-300 text-sm">/downloads</code>
              <span className="text-slate-500 text-xs">创建配置（app_id + platform 唯一）</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded">PUT</span>
              <code className="text-yellow-300 text-sm">/downloads/:id</code>
              <span className="text-slate-500 text-xs">更新配置（含各模式开关）</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded">DELETE</span>
              <code className="text-yellow-300 text-sm">/downloads/:id</code>
              <span className="text-slate-500 text-xs">删除配置（同时清理存储文件）</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded">POST</span>
              <code className="text-yellow-300 text-sm">/downloads/upload</code>
              <span className="text-slate-500 text-xs">上传文件（multipart，参数：file/app_id/platform/type=qrcode|file）</span>
            </div>
          </div>

          <p className="text-slate-400 text-sm">创建/更新配置的 Body 示例：</p>
          <CodeBlock code={`{
  "app_id": "x7k9m2abc",
  "platform": "app",            // app | pc | tv
  "enabled": 1,                 // 平台总开关 0/1
  "mode_url_enabled": 1,        // 网址直达开关
  "mode_qrcode_enabled": 1,     // 二维码开关
  "mode_file_enabled": 1,       // 文件下载开关
  "url": "https://example.com/app.apk",
  "qrcode_r2_key": "dl/.../qrcode-...",
  "qrcode_url": "https://.../preview/...",
  "file_r2_key": "dl/.../file-...",
  "file_name": "app-v1.0.0.apk",
  "file_size": 12345678,
  "file_version": "v1.0.0"
}`} />
        </div>

        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <h3 className="text-green-400 font-semibold mb-2">SDK 接入（推荐）</h3>
          <p className="text-slate-300 text-sm mb-3">在各程序前端首页 <code className="bg-slate-700 px-1 rounded text-yellow-300">&lt;body&gt;</code> 中引入以下代码，即可在页面左上方自动显示下载下拉窗口（无需手写 UI）：</p>

          <p className="text-slate-400 text-xs mb-1">方式 A：脚本标签自动初始化（最简单，推荐）</p>
          <CodeBlock code={`<script
  src="https://unified-auth-admin.pages.dev/sdk/download-widget.js"
  data-app-id="YOUR_APP_ID"
  defer
></script>`} />

          <p className="text-slate-400 text-xs mb-1 mt-3">方式 B：手动初始化（可自定义 apiBase / 位置 / 容器）</p>
          <CodeBlock code={`<script src="https://unified-auth-admin.pages.dev/sdk/download-widget.js"></script>
<script>
  new DownloadWidget({
    appId: 'YOUR_APP_ID',                          // 必填，在后台「应用管理」获取
    apiBase: 'https://unified-auth-admin.pages.dev', // 可选，默认即此地址
    position: 'top-left'                              // 可选，top-left | top-right
  }).init();
</script>`} />

          <p className="text-slate-400 text-xs mt-3">
            SDK 会自动调用 <code className="bg-slate-700 px-1 rounded text-yellow-300">/api/downloads/public/:app_id</code> 获取配置，仅显示已启用的平台和模式。点击「网址直达」跳转URL，点击「二维码」弹出图片，点击「文件下载」触发安装包下载。SDK 资源已配置 CORS，支持任意域名跨域加载。
          </p>
        </div>
      </Section>
    </div>
  );
}